import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { quizQuestions } from '../data/quizData'
import type { PlayerType, RootCode } from '../data/resultsData'
import {
  getVisitorId,
  getOrCreateSession,
  trackStep,
  completeSession as completeAnalyticsSession,
  trackCTAClick,
} from '../data/analyticsStore'

export type FunnelStep =
  | 'intro'
  | `q${number}`
  | 'loading'
  | 'results'
  | 'offer'

export interface QuizState {
  currentStep: FunnelStep
  answers: Record<number, string>
  playerType: PlayerType | null
  rootCode: RootCode | null
  primaryGoal: string | null
  commitLevel: string | null
  recommendedOffer: 17 | 37
  painTag: string | null
}

function resolvePlayerType(answers: Record<number, string>): PlayerType {
  const q14 = answers[14]
  if (q14 === 'TYPE_OBSERVADOR_ATRAPADO') return 'OBSERVADOR_ATRAPADO'
  if (q14 === 'TYPE_BUSCADOR_BUCLE') return 'BUSCADOR_BUCLE'
  if (q14 === 'TYPE_PROTECTOR_CONTROL') return 'PROTECTOR_CONTROL'
  if (q14 === 'TYPE_REBELDE_CANSADO') return 'REBELDE_CANSADO'

  const q4 = answers[4]
  const q3 = answers[3]
  const q7 = answers[7]

  if (q4 === 'PAIN_ANXIETY' || q4 === 'PAIN_FEAR') return 'PROTECTOR_CONTROL'
  if (q4 === 'PAIN_EMPTY' || q4 === 'PAIN_EXHAUSTED') return 'REBELDE_CANSADO'
  if (q3 === 'STYLE_OVERTHINKER') return 'OBSERVADOR_ATRAPADO'
  if (q7 === 'TRIED_EVERYTHING') return 'BUSCADOR_BUCLE'

  return 'OBSERVADOR_ATRAPADO'
}

function resolveRootCode(answers: Record<number, string>): RootCode {
  const q10 = answers[10]
  const map: Record<string, RootCode> = {
    MATRIX_MIND: 'MENTE',
    MATRIX_TIME: 'TIEMPO',
    MATRIX_IDENTITY: 'IDENTIDAD',
    MATRIX_FEAR: 'MIEDO',
    MATRIX_DISTRACTION: 'DISTRACCION',
  }
  return map[q10] || 'MENTE'
}

export function useQuizState() {
  const [currentStep, setCurrentStep] = useState<FunnelStep>('intro')
  const [answers, setAnswers] = useState<Record<number, string>>({})
  const sessionIdRef = useRef<string | null>(null)

  // Initialize analytics session
  useEffect(() => {
    const visitorId = getVisitorId()
    const session = getOrCreateSession(visitorId)
    sessionIdRef.current = session.id
  }, [])

  const trackAnalytics = useCallback(
    (step: string, data?: Record<string, string>) => {
      if (sessionIdRef.current) {
        trackStep(sessionIdRef.current, step, data)
      }
    },
    [],
  )

  const startQuiz = useCallback(() => {
    trackAnalytics('quiz_start')
    setCurrentStep('q1')
  }, [trackAnalytics])

  const answerQuestion = useCallback(
    (questionId: number, tag: string) => {
      setAnswers((prev) => ({ ...prev, [questionId]: tag }))

      const question = quizQuestions.find((q) => q.id === questionId)
      const answerText =
        question?.answers.find((a) => a.tag === tag)?.text || tag

      trackAnalytics(`question_${questionId}`, {
        questionId: String(questionId),
        tag,
        text: answerText,
      })

      if (questionId < quizQuestions.length) {
        setTimeout(() => {
          setCurrentStep(`q${questionId + 1}` as FunnelStep)
        }, 400)
      } else {
        trackAnalytics('quiz_complete')
        setTimeout(() => {
          setCurrentStep('loading')
        }, 400)
      }
    },
    [trackAnalytics],
  )

  const goToResults = useCallback(() => {
    trackAnalytics('result_viewed')
    setCurrentStep('results')
  }, [trackAnalytics])

  const goToOffer = useCallback(() => {
    // Complete session with resolved player type
    setCurrentStep('offer')
  }, [])

  const goToIntro = useCallback(() => {
    setCurrentStep('intro')
    setAnswers({})
  }, [])

  const computed = useMemo(() => {
    let playerType: PlayerType | null = null
    if (answers[14]) {
      playerType = resolvePlayerType(answers)
    }

    let rootCode: RootCode | null = null
    if (answers[10]) {
      rootCode = resolveRootCode(answers)
    }

    const commitLevel = answers[13] || null
    const recommendedOffer: 17 | 37 =
      commitLevel === 'COMMIT_10' ||
      commitLevel === 'COMMIT_20' ||
      commitLevel === 'COMMIT_30'
        ? 37
        : 17

    return {
      playerType,
      rootCode,
      primaryGoal: answers[12] || null,
      commitLevel,
      painTag: answers[4] || null,
      recommendedOffer,
    }
  }, [answers])

  // Complete analytics session when player type is resolved
  useEffect(() => {
    if (computed.playerType && sessionIdRef.current) {
      completeAnalyticsSession(sessionIdRef.current, computed.playerType)
    }
  }, [computed.playerType])

  const handleCTAClick = useCallback(() => {
    if (sessionIdRef.current) {
      trackCTAClick(sessionIdRef.current)
    }
    trackAnalytics('cta_clicked')
  }, [trackAnalytics])

  const currentQuestionIndex = useMemo(() => {
    const match = (currentStep as string).match(/^q(\d+)$/)
    return match ? parseInt(match[1], 10) : null
  }, [currentStep])

  return {
    currentStep,
    answers,
    currentQuestionIndex,
    ...computed,
    startQuiz,
    answerQuestion,
    goToResults,
    goToOffer,
    goToIntro,
    handleCTAClick,
  }
}
